import { AlertCircle, History, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/common/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/common/ui/tabs";
import { ProfileRevisionsDialog } from "@/profile-revisions";

import type { User, UserStatus, UserStatusFilter } from "./types";
import { USER_STATUS_FILTERS, USER_STATUSES } from "./types";
import { useUsers } from "./use-users";

const STATUS_LABEL: Record<UserStatusFilter, string> = {
  all: "All",
  waitlist: "Waitlist",
  active: "Active",
  suspended: "Suspended",
};

const STATUS_BADGE_VARIANT: Record<UserStatus, "secondary" | "default" | "destructive"> = {
  waitlist: "secondary",
  active: "default",
  suspended: "destructive",
};

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

export function UsersPage(): React.ReactElement {
  const {
    users,
    total,
    isLoading,
    error,
    filter,
    setFilter,
    refresh,
    changeStatus,
    lastError,
    clearLastError,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useUsers();

  /**
   * The subject whose revision history is open, or null. Holding the user
   * rather than a boolean keeps the dialog unmounted while closed, so it cannot
   * keep a previous subject's state or let an in-flight read land after
   * dismissal.
   */
  const [historyUser, setHistoryUser] = useState<User | null>(null);

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-center justify-end">
        <Button
          data-testid="users-refresh-button"
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={isLoading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Tabs
        value={filter}
        onValueChange={(value) => setFilter(value as UserStatusFilter)}
        data-testid="users-filter-tabs"
      >
        <TabsList>
          {USER_STATUS_FILTERS.map((f) => (
            <TabsTrigger key={f} value={f} data-testid={`users-filter-${f}`}>
              {STATUS_LABEL[f]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {error && (
        <div
          data-testid="users-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button
            data-testid="users-retry-button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={refresh}
          >
            Retry
          </Button>
        </div>
      )}

      {lastError && (
        <div
          data-testid="users-action-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{lastError.message}</span>
          <Button
            data-testid="users-action-error-dismiss"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={clearLastError}
          >
            Dismiss
          </Button>
        </div>
      )}

      {!error && users.length === 0 && !isLoading && (
        <div data-testid="users-empty" className="py-16 text-center text-muted-foreground">
          <p className="text-lg font-medium">No users found</p>
          <p className="text-sm">No users match the current filter.</p>
        </div>
      )}

      {users.length > 0 && (
        <UsersTable
          users={users}
          onChangeStatus={changeStatus}
          onViewHistory={setHistoryUser}
          total={total}
        />
      )}

      {hasMore && (
        <div className="flex justify-center">
          <Button
            data-testid="users-load-more-button"
            variant="outline"
            size="sm"
            onClick={loadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}

      {historyUser && (
        <ProfileRevisionsDialog
          userId={historyUser.id}
          userLabel={historyUser.email ?? historyUser.display_name ?? historyUser.id}
          onClose={() => setHistoryUser(null)}
        />
      )}
    </div>
  );
}

interface UsersTableProps {
  users: readonly User[];
  total: number;
  onChangeStatus: (id: string, status: UserStatus) => Promise<void>;
  onViewHistory: (user: User) => void;
}

function UsersTable({
  users,
  total,
  onChangeStatus,
  onViewHistory,
}: UsersTableProps): React.ReactElement {
  return (
    <div data-testid="users-table" className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead className="hidden sm:table-cell">Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden md:table-cell">Joined</TableHead>
            <TableHead className="w-[180px]">
              <span className="sr-only">Change status</span>
            </TableHead>
            <TableHead className="w-[56px]">
              <span className="sr-only">Revision history</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
              <TableCell className="font-medium">{user.email ?? "—"}</TableCell>
              <TableCell className="hidden sm:table-cell">{user.display_name ?? "—"}</TableCell>
              <TableCell>
                <Badge
                  variant={STATUS_BADGE_VARIANT[user.access_status]}
                  data-testid={`user-status-badge-${user.id}`}
                >
                  {user.access_status}
                </Badge>
              </TableCell>
              <TableCell className="hidden md:table-cell text-muted-foreground">
                {dateFormatter.format(new Date(user.created_at))}
              </TableCell>
              <TableCell>
                <Select
                  value={user.access_status}
                  onValueChange={(value) => void onChangeStatus(user.id, value as UserStatus)}
                >
                  <SelectTrigger
                    data-testid={`user-status-trigger-${user.id}`}
                    aria-label={`Change status for ${user.email ?? user.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_STATUSES.map((status) => (
                      <SelectItem
                        key={status}
                        value={status}
                        data-testid={`user-status-option-${user.id}-${status}`}
                      >
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Button
                  data-testid={`user-history-button-${user.id}`}
                  variant="ghost"
                  size="sm"
                  aria-label={`View revision history for ${user.email ?? user.id}`}
                  onClick={() => onViewHistory(user)}
                >
                  <History className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-muted-foreground" data-testid="users-total">
        Showing {users.length} of {total}
      </p>
    </div>
  );
}
